/**
 * Life Theme Identifier API Service
 * Hanterar all kommunikation med backend för livstema-identifiering
 */

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('therapy_auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const lifeThemeAPI = {
  /**
   * Hämta alla svarsskalemappningar
   */
  getAnswerScale: async () => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/answer-scale`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta alla livsteman
   */
  getAllThemes: async () => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta ett specifikt livstema
   */
  getTheme: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes/${themeId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta alla frågor för ett livstema
   */
  getThemeQuestions: async (themeId, mscrFirst = true) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/questions`, {
      params: { mscr_first: mscrFirst },
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta karusell med 5 obesvarade frågor från olika teman
   */
  getQuestionCarousel: async () => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/questions/carousel`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Skapa eller uppdatera ett svar
   */
  createOrUpdateAnswer: async (questionId, answerValue) => {
    const response = await axios.post(`${API_BASE}/api/life-theme-identifier/answers`, {
      question_id: questionId,
      answer_value: answerValue
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta användarens svar för ett tema
   */
  getUserAnswersForTheme: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/answers/theme/${themeId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta progress för ett tema
   */
  getThemeProgress: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/progress`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta sammanfattning för ett tema (när alla frågor är besvarade)
   */
  getThemeSummary: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/summary`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta översikt över alla teman med scores
   */
  getAllScores: async () => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/scores`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta score för ett specifikt tema
   */
  getThemeScore: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/scores/theme/${themeId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Radera alla svar för ett tema (för att kunna svara om)
   */
  resetThemeAnswers: async (themeId) => {
    const response = await axios.delete(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/answers`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Hämta utforskningsprogress för ett tema (AI deep dive)
   */
  getExplorationProgress: async (themeId) => {
    const response = await axios.get(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/exploration-progress`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

export default lifeThemeAPI;
